import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Button, Pagination, Select } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { DataTable, DataTableColumn, DataTableProps } from 'mantine-datatable';

import { FilterInput, Request } from '../../codegen/graphql';
import { TableWrapper, When } from '../common';
import { ExportCostOptionsButton } from '../common/ExportCostOptionsButton';
import { GET_PRICE_CHANGE_REQUESTS_TABLE_DATA } from '../../apollo/queries/priceChangeRequest';
import { showErrorNotification } from '../../notifications';
import { useExportRequests } from '../../hooks/useExportRequests';
import { ProcurementManagerColumnHeader } from './components/filter-columns/ProcurementManagerHeader';
import { StoreColumnHeader } from './components/filter-columns/StoreColumnHeader';
import { AdvertisingZoneHeader } from './components/filter-columns/AdvertisingZoneHeader';
import { SapOutcomeHeader } from './components/filter-columns/SapOutcomeHeader';
import { StatusHeader } from './components/filter-columns/StatusHeader';
import { FriendlyIdHeader } from './components/filter-columns/FriendlyIdHeader';
import { SkuHeader } from './components/filter-columns/SkuHeader';
import { ProductCategoryHeader } from './components/filter-columns/ProductCategoryHeader';
import { SurveyHeader } from './components/filter-columns/SurveyHeader';
import { ScopeHeader } from './components/filter-columns/ScopeHeader';
import { useRequestsTableStore } from '../../stores/RequestsTableStore';
import { useAuthStore } from '../../stores/auth.store';
import { useAuth } from '../../context/AuthProvider';
import {
    useRequestDrawer,
    type RequestDrawerOpenOptions,
} from '../../context/RequestDrawerContext';
import { GET_USER_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { useDataTableSorting } from '../../hooks/useDataTableSorting';
import { commonTableStyles } from '../../constants/tableStyles';
import DateRangePickerWithPresets from '../common/DateRangePickerWithPresets';
import { useUser } from '../../hooks/useUser';
import { useAppConfig } from '../../hooks/useAppConfig';
import {
    getBackendScopeFilter,
    getBackendStatusFilter,
    getBackendSurveyFilter,
    getUrgentFilter,
    getAutoDeclinedFilter,
} from './requestsTableRenderers';

const WRAPPER_OFFSET = '19rem';

export type ColumnBuilderCtx = {
    openRequestDrawer: (
        request: Request,
        options?: RequestDrawerOpenOptions,
    ) => void;
    headers: {
        FriendlyId: ReactNode;
        Sku: ReactNode;
        ProductCategory: ReactNode;
        Store: ReactNode;
        AdvertisingZone: ReactNode;
        Status: ReactNode;
        SapOutcome: ReactNode;
        ProcurementManager: ReactNode;
        Survey: ReactNode;
        Scope: ReactNode;
    };
    pricingBatchIndicators?: {
        vatPercentage: number;
        hideCostAndMargins: boolean;
    };
};

export type RenderBodyCtx = {
    readonly input: FilterInput;
    readonly columnCtx: ColumnBuilderCtx;
    readonly onTotalChange: (total: number) => void;
    readonly onRefresh: (refresh: () => void) => void;
    readonly onSelectedIdsChange: (ids: string[]) => void;
    readonly openRequestDrawer: (
        request: Request,
        options?: RequestDrawerOpenOptions,
    ) => void;
};

export type BaseRequestsTableProps = {
    readonly dateRange?: readonly [Date | null, Date | null];
    readonly userIds?: string[] | null;
    readonly onlyPendingResponsibleActions?: boolean;
    readonly showDateRangeSelector?: boolean;
    readonly excludeCreatedDateFromFilter?: boolean;
    readonly buildColumns: (
        ctx: ColumnBuilderCtx,
    ) => DataTableColumn<Request>[];
    readonly pinLastColumn?: boolean;
    readonly tableHeightOffset?: string;
    readonly renderBody?: (ctx: RenderBodyCtx) => ReactNode;
    readonly detachedBody?: boolean;
    readonly toolbarTrailing?: ReactNode;
};

function BaseRequestsTable({
    dateRange: initialDateRange,
    userIds,
    onlyPendingResponsibleActions = false,
    showDateRangeSelector = true,
    excludeCreatedDateFromFilter = false,
    buildColumns,
    pinLastColumn = false,
    tableHeightOffset,
    renderBody,
    detachedBody = false,
    toolbarTrailing,
}: BaseRequestsTableProps) {
    const { pathname } = useLocation();
    const [, setSearchParams] = useSearchParams();
    const { authUser } = useAuth();
    const standInForUserIds = useAuthStore(state => state.standInForUserIds);
    const { isDivisionManager, isOpsExecutive, isStoreManager } = useUser();
    const { vatPercentage } = useAppConfig();

    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        globalFilter,
        setGlobalFilter,
        sapOutcomeFilter,
        setSapOutcomeFilter,
        columnFilters,
        setColumnFilters,
        advertisingZoneFilter,
        setAdvertisingZoneFilter,
        procurementManagerFilter,
        setProcurementManagerFilter,
        productCategoryFilter,
        setProductCategoryFilter,
        operationsAreaFilter,
        setOperationsAreaFilter,
        divisionFilter,
        setDivisionFilter,
        storeFilter,
        setStoreFilter,
        friendlyIdFilter,
        setFriendlyIdFilter,
        dateRange,
        setDateRange,
    } = useRequestsTableStore();

    const [selectedRecords, setSelectedRecords] = useState<Request[]>([]);

    const { sortStatus, handleSortStatusChange } = useDataTableSorting<Request>(
        {
            sorting,
            setSorting,
        },
    );

    const [debouncedGlobalFilter] = useDebouncedValue(globalFilter, 500);
    const createdAtStart = dateRange?.[0]
        ? dayjs(dateRange[0]).startOf('day').toDate()
        : null;
    const createdAtEnd = dateRange?.[1]
        ? dayjs(dateRange[1]).endOf('day').toDate()
        : null;

    const productSkuFilter = useMemo(
        () =>
            columnFilters.find(filter => filter.id === 'sku')?.value as
                | string[]
                | null,
        [columnFilters],
    );

    const [statusFilter, setStatusFilter] = useState<string[] | null>(null);
    const [surveyFilter, setSurveyFilter] = useState<string[] | null>(null);
    const [scopeFilter, setScopeFilter] = useState<string[] | null>(null);
    const [dateRangeReady, setDateRangeReady] = useState(false);
    const [hierarchyReady, setHierarchyReady] = useState(false);
    const [standInDivisionIds, setStandInDivisionIds] = useState<
        string[] | null
    >(null);
    const previousAuthUserIdRef = useRef<string | null | undefined>(undefined);

    const queryInput = useMemo<FilterInput>(
        () => ({
            take: pagination.pageSize,
            skip: pagination.pageIndex * pagination.pageSize,
            createdAtStart: excludeCreatedDateFromFilter
                ? null
                : createdAtStart,
            createdAtEnd: excludeCreatedDateFromFilter ? null : createdAtEnd,
            userIds: userIds && userIds.length > 0 ? userIds : null,
            onlyPendingResponsibleActions,
            status: getBackendStatusFilter(statusFilter),
            urgent: getUrgentFilter(statusFilter),
            isAutoDeclined: getAutoDeclinedFilter(statusFilter),
            sort: sorting.length > 0 ? sorting[0] : undefined,
            globalFilter: debouncedGlobalFilter ?? null,
            friendlyId: friendlyIdFilter?.[0] ?? null,
            productSku: productSkuFilter?.[0] ?? null,
            sapOutcome: sapOutcomeFilter,
            advertisingZone: advertisingZoneFilter,
            procurementManagerIds: procurementManagerFilter,
            productCategory: productCategoryFilter,
            operationsAreaIds: operationsAreaFilter,
            divisionIds: divisionFilter,
            storeIds: storeFilter,
            standInDivisionIds:
                standInDivisionIds && standInDivisionIds.length > 0
                    ? standInDivisionIds
                    : null,
            priceChangeScope: getBackendScopeFilter(scopeFilter),
            isFromPriceSurvey: getBackendSurveyFilter(surveyFilter),
        }),
        [
            pagination.pageSize,
            pagination.pageIndex,
            createdAtStart,
            createdAtEnd,
            excludeCreatedDateFromFilter,
            userIds,
            onlyPendingResponsibleActions,
            statusFilter,
            sorting,
            debouncedGlobalFilter,
            friendlyIdFilter,
            productSkuFilter,
            sapOutcomeFilter,
            advertisingZoneFilter,
            procurementManagerFilter,
            productCategoryFilter,
            operationsAreaFilter,
            divisionFilter,
            storeFilter,
            standInDivisionIds,
            scopeFilter,
            surveyFilter,
        ],
    );

    const { data, loading, error, refetch } = useQuery(
        GET_PRICE_CHANGE_REQUESTS_TABLE_DATA,
        {
            fetchPolicy: 'no-cache',
            notifyOnNetworkStatusChange: true,
            pollInterval: 60000,
            skip:
                !!renderBody ||
                !dateRangeReady ||
                !hierarchyReady ||
                (onlyPendingResponsibleActions &&
                    (!userIds || userIds.length === 0)),
            variables: { input: queryInput },
        },
    );

    const [externalTotal, setExternalTotal] = useState(0);
    const [externalSelectedIds, setExternalSelectedIds] = useState<string[]>(
        [],
    );
    const externalRefreshRef = useRef<(() => void) | null>(null);
    const handleExternalRefresh = useCallback((refresh: () => void) => {
        externalRefreshRef.current = refresh;
    }, []);

    const handleRefreshButtonClick = useCallback(() => {
        if (renderBody) {
            const runExternalRefresh = externalRefreshRef.current;
            if (runExternalRefresh) {
                runExternalRefresh();
            } else {
                notifications.show({
                    title: 'Refresh',
                    message: 'Data is still loading. Try again in a moment.',
                    color: 'yellow',
                });
            }
        } else {
            refetch();
        }
    }, [renderBody, refetch]);

    const { exporting, exportRequests } = useExportRequests();
    const { data: userHierarchyData, loading: hierarchyLoading } = useQuery(
        GET_USER_HIERARCHY,
        {
            variables: { userId: authUser?.id },
            skip: !authUser?.id,
            fetchPolicy: 'network-only',
        },
    );
    const [fetchUserHierarchy] = useLazyQuery(GET_USER_HIERARCHY, {
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (initialDateRange) {
            setDateRange([...initialDateRange]);
        } else {
            setDateRange([null, null]);
        }
        setDateRangeReady(true);
    }, [initialDateRange, setDateRange]);

    useEffect(() => {
        const coverageKey = standInForUserIds.join(',');
        const nextKey = `${authUser?.id ?? ''}|${coverageKey}`;
        if (nextKey === previousAuthUserIdRef.current) {
            return;
        }
        if (previousAuthUserIdRef.current !== undefined) {
            setHierarchyReady(false);
            setDivisionFilter(null);
            setOperationsAreaFilter(null);
            setStoreFilter(null);
            setStandInDivisionIds(null);
        }
        previousAuthUserIdRef.current = nextKey;
    }, [
        authUser?.id,
        standInForUserIds,
        setDivisionFilter,
        setOperationsAreaFilter,
        setStoreFilter,
    ]);

    useEffect(() => {
        if (hierarchyReady || hierarchyLoading) {
            return undefined;
        }

        let cancelled = false;

        const applyHierarchy = async () => {
            const ownHierarchy = userHierarchyData?.getUserHierarchy;
            const ownDivisionIds = ownHierarchy?.divisionIds ?? [];
            const ownOpsAreaIds = ownHierarchy?.opsAreaIds ?? [];
            const ownStoreIds = ownHierarchy?.storeIds ?? [];

            const coveredHierarchies =
                standInForUserIds.length > 0
                    ? await Promise.all(
                          standInForUserIds
                              .filter(
                                  coveredUserId =>
                                      Boolean(coveredUserId) &&
                                      coveredUserId !== authUser?.id,
                              )
                              .map(coveredUserId =>
                                  fetchUserHierarchy({
                                      variables: { userId: coveredUserId },
                                  }),
                              ),
                      )
                    : [];

            const extraDivisionIds = coveredHierarchies
                .flatMap(
                    coveredHierarchy =>
                        coveredHierarchy.data?.getUserHierarchy?.divisionIds ??
                        [],
                )
                .filter(
                    (divisionId, index, ids) =>
                        !ownDivisionIds.includes(divisionId) &&
                        ids.indexOf(divisionId) === index,
                );

            if (!cancelled) {
                if (ownHierarchy && authUser?.id === ownHierarchy.userId) {
                    if (isDivisionManager && ownDivisionIds.length > 0) {
                        setDivisionFilter([
                            ...ownDivisionIds,
                            ...extraDivisionIds,
                        ]);
                    } else if (isOpsExecutive && ownOpsAreaIds.length > 0) {
                        setOperationsAreaFilter(ownOpsAreaIds);
                        setStandInDivisionIds(
                            extraDivisionIds.length > 0
                                ? extraDivisionIds
                                : null,
                        );
                    } else if (isStoreManager && ownStoreIds.length > 0) {
                        setStoreFilter(ownStoreIds);
                    }
                }

                setHierarchyReady(true);
            }
        };

        applyHierarchy().catch(() => {
            if (!cancelled) {
                setHierarchyReady(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        hierarchyLoading,
        hierarchyReady,
        userHierarchyData,
        authUser?.id,
        standInForUserIds,
        fetchUserHierarchy,
        isDivisionManager,
        isOpsExecutive,
        isStoreManager,
        setDivisionFilter,
        setOperationsAreaFilter,
        setStoreFilter,
    ]);

    useEffect(() => {
        if (error) {
            showErrorNotification('Error fetching price change requests');
        }
    }, [error]);

    const isRequestsPage = useMemo(
        () => pathname.includes('requests'),
        [pathname],
    );
    const { totalRequests, requests } = useMemo(
        () => ({
            totalRequests: renderBody
                ? externalTotal
                : data?.getPriceChangeRequestsTableData?.pagination?.total ?? 0,
            requests: data?.getPriceChangeRequestsTableData?.requests ?? [],
        }),
        [data, renderBody, externalTotal],
    );

    const selectedRowCount = renderBody
        ? externalSelectedIds.length
        : selectedRecords.length;

    const { open: openDrawerContext } = useRequestDrawer();

    const openRequestDrawer = useCallback(
        (row: Request, options?: RequestDrawerOpenOptions) => {
            openDrawerContext(row, undefined, options);
            setSearchParams({ requestId: row.id });
        },
        [setSearchParams, openDrawerContext],
    );

    const handleRowClick = (record: Request) => {
        openRequestDrawer(record);
    };

    const handleExport = useCallback(
        async (includeCostPricesAndMargins: boolean) => {
            const selectedIds = renderBody
                ? externalSelectedIds
                : selectedRecords.map(record => record.id);
            const requestIdsToExport: string[] | null =
                selectedIds.length > 0 ? selectedIds : null;

            const filters = {
                ...(excludeCreatedDateFromFilter ? {} : { dateRange }),
                status: getBackendStatusFilter(statusFilter),
                isAutoDeclined: getAutoDeclinedFilter(statusFilter),
                sapOutcome: sapOutcomeFilter ?? undefined,
                advertisingZone: advertisingZoneFilter ?? undefined,
                procurementManagerIds: procurementManagerFilter ?? undefined,
                productCategory: productCategoryFilter ?? undefined,
                operationsAreaIds: operationsAreaFilter ?? undefined,
                divisionIds: divisionFilter ?? undefined,
                standInDivisionIds: standInDivisionIds ?? undefined,
                storeIds: storeFilter ?? undefined,
                priceChangeScope: getBackendScopeFilter(scopeFilter),
                isFromPriceSurvey: getBackendSurveyFilter(surveyFilter),
                globalFilter: debouncedGlobalFilter ?? undefined,
                friendlyId:
                    friendlyIdFilter && friendlyIdFilter.length > 0
                        ? friendlyIdFilter[0]
                        : undefined,
                productSku:
                    productSkuFilter && productSkuFilter.length > 0
                        ? productSkuFilter[0]
                        : undefined,
            };

            try {
                await exportRequests(
                    requestIdsToExport,
                    filters,
                    includeCostPricesAndMargins,
                );
                if (!renderBody) {
                    setSelectedRecords([]);
                }
            } catch (exportError: unknown) {
                console.error(
                    'Price change request export failed:',
                    exportError,
                );
            }
        },
        [
            renderBody,
            externalSelectedIds,
            selectedRecords,
            exportRequests,
            dateRange,
            excludeCreatedDateFromFilter,
            statusFilter,
            sapOutcomeFilter,
            advertisingZoneFilter,
            procurementManagerFilter,
            productCategoryFilter,
            operationsAreaFilter,
            divisionFilter,
            storeFilter,
            standInDivisionIds,
            scopeFilter,
            surveyFilter,
            debouncedGlobalFilter,
            friendlyIdFilter,
            productSkuFilter,
        ],
    );

    const ProcurementManagerHeaderComponent = useMemo(
        () => (
            <ProcurementManagerColumnHeader
                currentValue={procurementManagerFilter}
                onChange={setProcurementManagerFilter}
            />
        ),
        [procurementManagerFilter, setProcurementManagerFilter],
    );

    const StoreHeaderComponent = useMemo(
        () => (
            <StoreColumnHeader
                currentOperationsAreaIds={operationsAreaFilter}
                currentDivisionIds={divisionFilter}
                currentStoreIds={storeFilter}
                onChange={(
                    operationsAreaIds: string[] | null,
                    divisionIds: string[] | null,
                    storeIds: string[] | null,
                ) => {
                    setOperationsAreaFilter(operationsAreaIds);
                    setDivisionFilter(divisionIds);
                    setStoreFilter(storeIds);
                    setStandInDivisionIds(null);
                }}
            />
        ),
        [operationsAreaFilter, divisionFilter, storeFilter],
    );

    const AdvertisingZoneHeaderComponent = useMemo(
        () => (
            <AdvertisingZoneHeader
                currentValue={advertisingZoneFilter}
                onChange={setAdvertisingZoneFilter}
            />
        ),
        [advertisingZoneFilter, setAdvertisingZoneFilter],
    );

    const SapOutcomeHeaderComponent = useMemo(
        () => (
            <SapOutcomeHeader
                currentValue={sapOutcomeFilter}
                onChange={setSapOutcomeFilter}
            />
        ),
        [sapOutcomeFilter],
    );

    const StatusHeaderComponent = useMemo(
        () => (
            <StatusHeader
                currentValue={statusFilter}
                onChange={setStatusFilter}
            />
        ),
        [statusFilter],
    );

    const SurveyHeaderComponent = useMemo(
        () => (
            <SurveyHeader
                currentValue={surveyFilter}
                onChange={setSurveyFilter}
            />
        ),
        [surveyFilter],
    );

    const ScopeHeaderComponent = useMemo(
        () => (
            <ScopeHeader currentValue={scopeFilter} onChange={setScopeFilter} />
        ),
        [scopeFilter],
    );

    const ProductCategoryHeaderComponent = useMemo(
        () => (
            <ProductCategoryHeader
                currentValue={productCategoryFilter}
                onChange={setProductCategoryFilter}
            />
        ),
        [productCategoryFilter],
    );

    const FriendlyIdHeaderComponent = useMemo(
        () => (
            <FriendlyIdHeader
                currentValue={friendlyIdFilter}
                onChange={setFriendlyIdFilter}
            />
        ),
        [friendlyIdFilter],
    );

    const handleSkuHeaderChange = useCallback(
        (value: string[] | null) => {
            const other = columnFilters.filter(f => f.id !== 'sku');
            if (!value || value.length === 0) {
                setColumnFilters(other);
            } else {
                setColumnFilters([...other, { id: 'sku', value }]);
            }
        },
        [columnFilters, setColumnFilters],
    );

    const SkuHeaderComponent = useMemo(
        () => (
            <SkuHeader
                currentValue={productSkuFilter as string[] | null}
                onChange={handleSkuHeaderChange}
            />
        ),
        [productSkuFilter, handleSkuHeaderChange],
    );

    const columnCtx = useMemo<ColumnBuilderCtx>(
        () => ({
            openRequestDrawer,
            pricingBatchIndicators: {
                vatPercentage,
                hideCostAndMargins: isStoreManager,
            },
            headers: {
                FriendlyId: FriendlyIdHeaderComponent,
                Sku: SkuHeaderComponent,
                ProductCategory: ProductCategoryHeaderComponent,
                Store: StoreHeaderComponent,
                AdvertisingZone: AdvertisingZoneHeaderComponent,
                Status: StatusHeaderComponent,
                SapOutcome: SapOutcomeHeaderComponent,
                ProcurementManager: ProcurementManagerHeaderComponent,
                Survey: SurveyHeaderComponent,
                Scope: ScopeHeaderComponent,
            },
        }),
        [
            openRequestDrawer,
            FriendlyIdHeaderComponent,
            SkuHeaderComponent,
            ProductCategoryHeaderComponent,
            StoreHeaderComponent,
            AdvertisingZoneHeaderComponent,
            StatusHeaderComponent,
            SapOutcomeHeaderComponent,
            ProcurementManagerHeaderComponent,
            SurveyHeaderComponent,
            ScopeHeaderComponent,
            vatPercentage,
            isStoreManager,
        ],
    );

    const resolvedColumns = useMemo(
        () => buildColumns(columnCtx),
        [buildColumns, columnCtx],
    );

    const filteredRequests = useMemo(() => {
        if (
            !statusFilter ||
            statusFilter.length === 0 ||
            statusFilter.includes('All')
        ) {
            return requests;
        }
        let filtered = requests;
        if (statusFilter.includes('Urgent')) {
            filtered = filtered.filter((request: Request) => request.isUrgent);
        }
        return filtered;
    }, [requests, statusFilter]);

    const { buttonText, buttonClassName, exportTooltipLabel } = useMemo(() => {
        let text;
        if (exporting) {
            text = 'Exporting...';
        } else if (selectedRowCount > 0) {
            text = `Export Selected (${selectedRowCount}) row${
                selectedRowCount !== 1 ? 's' : ''
            }`;
        } else {
            text = 'Export All';
        }

        const className = exporting
            ? 'text-gray-400 border-gray-300 cursor-not-allowed'
            : 'text-primary border-primary hover:bg-primary/10';
        const rowSuffix = selectedRowCount !== 1 ? 's' : '';
        const tooltipLabel =
            selectedRowCount === 0
                ? 'Click to export all requests'
                : `Click to export ${selectedRowCount} selected row${rowSuffix}`;

        return {
            buttonText: text,
            buttonClassName: className,
            exportTooltipLabel: tooltipLabel,
        };
    }, [exporting, selectedRowCount]);

    const dateRangeLabel =
        dateRange[0] && dateRange[1]
            ? `${dayjs(dateRange[0]).format('MMM D')} - ${dayjs(
                  dateRange[1],
              ).format('MMM D')}`
            : 'Date Range';

    const resolvedTableHeightOffset =
        tableHeightOffset ?? (isRequestsPage ? '15rem' : WRAPPER_OFFSET);

    return (
        <TableWrapper
            borderRadius="rounded-xl rounded-t-none"
            searchPlaceholder="Search Requests"
            buttons={
                <div className="ml-auto flex w-fit max-w-full shrink-0 flex-wrap items-center justify-end gap-5">
                    <ExportCostOptionsButton
                        label={buttonText}
                        disabled={exporting}
                        loading={exporting}
                        tooltipLabel={exportTooltipLabel}
                        onExport={handleExport}
                        hideCostAndMargins={isStoreManager}
                        className={buttonClassName}
                    />

                    {toolbarTrailing}

                    <When condition={showDateRangeSelector}>
                        <DateRangePickerWithPresets
                            value={dateRange}
                            onChange={setDateRange}
                            buttonLabel={dateRangeLabel}
                            buttonSize="sm"
                        />
                    </When>
                    <Button
                        variant="subtle"
                        className="shrink-0 text-primary rounded-md p-1 w-min h-min"
                        aria-label="Refresh"
                        onClick={handleRefreshButtonClick}
                    >
                        <IconRefresh size={25} stroke={1.5} aria-hidden />
                    </Button>
                </div>
            }
            tableHeightOffset={resolvedTableHeightOffset}
            onSearchChange={setGlobalFilter}
            searchValue={globalFilter}
            detachedBody={detachedBody}
            pagination={
                <div className="flex items-center gap-4 w-full justify-end">
                    <Select
                        data={['10', '20', '50', '100']}
                        value={pagination.pageSize.toString()}
                        onChange={value => {
                            if (value) {
                                setPagination({
                                    pageIndex: 0,
                                    pageSize: parseInt(value, 10),
                                });
                            }
                        }}
                        size="xs"
                        style={{ width: 80 }}
                    />
                    <Pagination
                        value={pagination.pageIndex + 1}
                        total={Math.ceil(totalRequests / pagination.pageSize)}
                        onChange={p =>
                            setPagination({
                                pageIndex: p - 1,
                                pageSize: pagination.pageSize,
                            })
                        }
                        size="sm"
                    />
                    <span className="text-xs text-gray-500 ml-2">
                        {totalRequests === 0
                            ? '0 of 0'
                            : `${
                                  pagination.pageSize * pagination.pageIndex + 1
                              }-${Math.min(
                                  pagination.pageSize *
                                      (pagination.pageIndex + 1),
                                  totalRequests,
                              )} of ${totalRequests}`}
                    </span>
                </div>
            }
        >
            <When condition={!!renderBody}>
                {renderBody?.({
                    input: queryInput,
                    columnCtx,
                    onTotalChange: setExternalTotal,
                    onRefresh: handleExternalRefresh,
                    onSelectedIdsChange: setExternalSelectedIds,
                    openRequestDrawer,
                })}
            </When>
            <When condition={!renderBody}>
                <DataTable
                    {...({
                        withTableBorder: true,
                        striped: true,
                        verticalSpacing: 'xs',
                        fz: 'sm',
                        withColumnBorders: false,
                        noRecordsText: 'No records to display',
                        highlightOnHover: true,
                        records: filteredRequests,
                        columns: resolvedColumns,
                        fetching: loading,
                        selectedRecords,
                        onSelectedRecordsChange: setSelectedRecords,
                        onRowClick: ({ record }: { record: Request }) =>
                            handleRowClick(record),
                        idAccessor: 'id',
                        onSortStatusChange: handleSortStatusChange,
                        pinLastColumn,
                        rowClassName: ({ id }: Request) =>
                            selectedRecords.some(r => r.id === id)
                                ? 'bg-blue-50 hover:bg-blue-100'
                                : 'bg-white hover:bg-gray-50 cursor-pointer',
                        classNames: {
                            table: 'selection-column-fix',
                        },
                        styles: commonTableStyles,
                        ...(sortStatus && { sortStatus }),
                    } as DataTableProps<Request>)}
                />
            </When>
        </TableWrapper>
    );
}

export default BaseRequestsTable;
